<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import {
  ROUND_ROBIN,
  EARLIEST_CREATED,
} from 'dashboard/routes/dashboard/settings/assignmentPolicy/constants';

import AssignmentPolicyForm from 'dashboard/routes/dashboard/settings/assignmentPolicy/pages/components/AgentAssignmentPolicyForm.vue';
import ConfirmInboxDialog from 'dashboard/routes/dashboard/settings/assignmentPolicy/pages/components/ConfirmInboxDialog.vue';

const BASE_KEY = 'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const uiFlags = useMapGetter('assignmentPolicies/getUIFlags');
const inboxes = useMapGetter('inboxes/getAllInboxes');
const inboxUiFlags = useMapGetter('assignmentPolicies/getInboxUiFlags');
const selectedPolicyById = useMapGetter(
  'assignmentPolicies/getAssignmentPolicyById'
);

const routeId = computed(() => route.params.id);
const selectedPolicy = computed(() => selectedPolicyById.value(routeId.value));

const confirmInboxDialogRef = ref(null);
const inboxLinkedPolicy = ref(null);

const goBack = () => {
  router.push({ name: 'agent_assignment_policy_index' });
};

const buildInboxList = allInboxes =>
  allInboxes?.map(({ name, id, email, phoneNumber, channelType, medium }) => ({
    name,
    id,
    email,
    phoneNumber,
    icon: getInboxIconByType(channelType, medium, 'line'),
  })) || [];

const policyInboxes = computed(() =>
  buildInboxList(selectedPolicy.value?.inboxes)
);

const inboxList = computed(() =>
  buildInboxList(
    inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name))
  )
);

const formData = computed(() => ({
  name: selectedPolicy.value?.name || '',
  description: selectedPolicy.value?.description || '',
  enabled: selectedPolicy.value?.enabled || false,
  assignmentOrder: selectedPolicy.value?.assignmentOrder || ROUND_ROBIN,
  conversationPriority:
    selectedPolicy.value?.conversationPriority || EARLIEST_CREATED,
  fairDistributionLimit: selectedPolicy.value?.fairDistributionLimit || 10,
  fairDistributionWindow: selectedPolicy.value?.fairDistributionWindow || 60,
}));

const handleDeleteInbox = inboxId =>
  store.dispatch('assignmentPolicies/removeInboxPolicy', {
    policyId: selectedPolicy.value?.id,
    inboxId,
  });

const setInboxPolicy = async (inboxId, policyId) => {
  try {
    await store.dispatch('assignmentPolicies/setInboxPolicy', {
      inboxId,
      policyId,
    });
    useAlert(t(`${BASE_KEY}.FORM.INBOXES.API.SUCCESS_MESSAGE`));
    await store.dispatch(
      'assignmentPolicies/getInboxes',
      Number(routeId.value)
    );
    return true;
  } catch (error) {
    useAlert(t(`${BASE_KEY}.FORM.INBOXES.API.ERROR_MESSAGE`));
    return false;
  }
};

const handleAddInbox = async inbox => {
  try {
    const policy = await store.dispatch('assignmentPolicies/getInboxPolicy', {
      inboxId: inbox?.id,
    });

    if (policy?.id !== selectedPolicy.value?.id) {
      inboxLinkedPolicy.value = {
        ...policy,
        assignedInboxCount: policy.assignedInboxCount - 1,
      };
      confirmInboxDialogRef.value.openDialog(inbox);
      return;
    }
  } catch (error) {
    // If getInboxPolicy fails, continue to setInboxPolicy
  }

  await setInboxPolicy(inbox?.id, selectedPolicy.value?.id);
};

const handleConfirmAddInbox = async inboxId => {
  const success = await setInboxPolicy(inboxId, selectedPolicy.value?.id);

  if (success) {
    await store.dispatch('assignmentPolicies/updateInboxPolicy', {
      policy: inboxLinkedPolicy.value,
    });
    store.dispatch(
      'assignmentPolicies/getInboxes',
      inboxLinkedPolicy.value?.id
    );
    inboxLinkedPolicy.value = null;
    confirmInboxDialogRef.value.closeDialog();
  }
};

const handleSubmit = async formState => {
  try {
    await store.dispatch('assignmentPolicies/update', {
      id: selectedPolicy.value?.id,
      ...formState,
    });
    useAlert(t(`${BASE_KEY}.EDIT.API.SUCCESS_MESSAGE`));
  } catch {
    useAlert(t(`${BASE_KEY}.EDIT.API.ERROR_MESSAGE`));
  }
};

const fetchPolicyData = async () => {
  if (!routeId.value) return;

  if (!selectedPolicy.value?.id)
    await store.dispatch('assignmentPolicies/show', routeId.value);

  await store.dispatch('assignmentPolicies/getInboxes', Number(routeId.value));
};

watch(routeId, fetchPolicyData, { immediate: true });
</script>

<template>
  <div class="policy-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <div class="flex items-center justify-between w-full">
        <button
          class="flex items-center gap-2 text-sm mb-3 transition-colors"
          style="color: #6b7280"
          @click="goBack"
        >
          <span class="i-ph-arrow-left-bold size-4" />
          {{
            $t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.TITLE')
          }}
        </button>
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
            style="
              background: linear-gradient(to bottom right, #f59e0b, #0058d8);
              box-shadow: 0 10px 15px -3px rgba(245, 158, 11, 0.25);
            "
          >
            <span class="i-ph-pencil-simple-bold text-white size-5" />
          </div>
          <div>
            <h1 class="text-xl font-semibold" style="color: #111827">
              {{
                $t(
                  'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.EDIT.HEADER.TITLE'
                )
              }}
            </h1>
            <p v-if="selectedPolicy?.name" class="text-sm text-n-slate-11 mt-1">
              {{ selectedPolicy.name }}
            </p>
          </div>
        </div>
      </div>
    </header>

    <!-- Loading -->
    <div
      v-if="uiFlags.isFetchingItem"
      class="flex-1 flex items-center justify-center"
    >
      <div class="ws-spinner" />
    </div>

    <!-- Content -->
    <div v-else class="ws-content flex-1 overflow-y-auto p-6">
      <div class="form-container max-w-4xl">
        <AssignmentPolicyForm
          :key="routeId"
          mode="EDIT"
          :initial-data="formData"
          :policy-inboxes="policyInboxes"
          :inbox-list="inboxList"
          show-inbox-section
          :is-loading="uiFlags.isUpdating"
          :is-inbox-loading="inboxUiFlags.isFetching"
          @submit="handleSubmit"
          @add-inbox="handleAddInbox"
          @delete-inbox="handleDeleteInbox"
        />
      </div>
    </div>

    <ConfirmInboxDialog
      ref="confirmInboxDialogRef"
      @add="handleConfirmAddInbox"
    />
  </div>
</template>

<style scoped>
.policy-page,
.dark .policy-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc-bar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.ws-content {
  background: #ffffff;
}
.ws-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.ws-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.ws-search::placeholder {
  color: #9ca3af !important;
}
.ws-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.ws-btn:hover {
  background: #1f2937;
}
.ws-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}
.ws-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
.ws-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: ws-spin 0.9s linear infinite;
}
@keyframes ws-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Assignment policy component-level light theme */
.policy-page {
  --wlab-bg-tertiary: #f9fafb;
  --wlab-bg-hover: #fff7ed;
  --wlab-border-default: #e5e7eb;
  --wlab-text-muted: #9ca3af;
  --wlab-text-secondary: #0066ff;
  --wlab-shadow-card: 0 1px 3px rgba(15, 23, 42, 0.08),
    0 1px 2px rgba(15, 23, 42, 0.05);
  --wlab-shadow-card-hover: 0 14px 24px -16px rgba(15, 23, 42, 0.28);
}

:deep(.wlab-card-modern) {
  background: #ffffff !important;
  outline-color: #e5e7eb !important;
}

:deep(.wlab-card-modern:hover) {
  outline-color: #0066ff !important;
}

:deep(.radio-card) {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
}

:deep(.radio-card--active) {
  background: #fff7ed !important;
  border-color: #0066ff !important;
}

:deep(.radio-input) {
  accent-color: #0066ff !important;
}

:deep(.outline-n-container) {
  outline-color: #e5e7eb !important;
}
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2),
:deep(.bg-n-solid-3),
:deep(.bg-n-background) {
  background: #ffffff !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.border-n-weak),
:deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}
:deep(.bg-n-alpha-1),
:deep(.bg-n-alpha-2) {
  background: #f9fafb !important;
}

:deep(input:not(.reset-base)),
:deep(select:not(.reset-base)),
:deep(textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
</style>
